import { createContext, useContext, type ReactNode } from "react"
import { countLibraryKinds, type LibraryItem, type LibraryKindCounts } from "@/components/library/library-item"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

/** Порядок как в макете: сперва видео, потом кадры, потом звук. */
export const LIBRARY_FILTER_KINDS = ["video", "image", "audio"] as const

export type LibraryKindFilterValue = "all" | (typeof LIBRARY_FILTER_KINDS)[number]

/**
 * Активный фильтр приходит контекстом, а не пропами: его обязаны учитывать и плитки,
 * и заголовки групп, и они разбросаны по четырём узлам папок (шот, сцена, доска,
 * сущность). Через пропы это означало бы одинаковую правку в каждом узле, через
 * контекст — правку в двух общих компонентах.
 */
const LibraryKindFilterContext = createContext<LibraryKindFilterValue>("all")

export function LibraryKindFilterProvider({ value, children }: { value: LibraryKindFilterValue; children: ReactNode }) {
  return <LibraryKindFilterContext.Provider value={value}>{children}</LibraryKindFilterContext.Provider>
}

export function useLibraryKindFilter(): LibraryKindFilterValue {
  return useContext(LibraryKindFilterContext)
}

/**
 * Папки под фильтром по типу медиа не проходят намеренно: «покажи только видео»
 * в папке эпизода — это пустой ответ, а не список подпапок, в которых видео может
 * и не быть. Счётчики чипов при этом честные, и в такой папке все три типа просто
 * недоступны для нажатия.
 */
export function filterLibraryItems(items: LibraryItem[], filter: LibraryKindFilterValue): LibraryItem[] {
  if (filter === "all") return items
  return items.filter((item) => item.kind === filter)
}

/**
 * Фильтр по типу ассета. Виден всегда и всегда всеми четырьмя чипами: раньше он
 * пропадал при одном типе в папке, и шапка прыгала при каждом переходе. Тип, которого
 * в папке нет, не нажимается — вместо исчезновения он объясняет, что искать нечего.
 */
export function LibraryKindFilter({
  items,
  value,
  onChange,
}: {
  items: LibraryItem[]
  value: LibraryKindFilterValue
  onChange: (next: LibraryKindFilterValue) => void
}) {
  const { t } = useTranslation(["workspace"])
  const counts: LibraryKindCounts = countLibraryKinds(items)
  // Подписи вкладок свои, не из library.types: в фильтре они во множественном числе.
  const chips: { key: LibraryKindFilterValue; label: string; count: number }[] = [
    { key: "all", label: t("library.filters.all"), count: items.length },
    ...LIBRARY_FILTER_KINDS.map((kind) => ({
      key: kind as LibraryKindFilterValue,
      label: t(`library.filters.${kind}`),
      count: counts[kind],
    })),
  ]

  return (
    <span
      className="inline-flex max-w-full shrink flex-wrap items-center gap-0.5 rounded-lg bg-ui-surface-sunken p-0.5"
      role="group"
      aria-label={t("library.filters.label")}
    >
      {chips.map((chip) => {
        const active = chip.key === value
        // Пустой тип не выключаем, пока он выбран: иначе фильтр нельзя было бы снять
        // с клавиатуры — кнопка исчезала бы из обхода ровно в тот момент, когда она нужна.
        const disabled = chip.count === 0 && chip.key !== "all" && !active
        return (
          <button
            key={chip.key}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(chip.key)}
            className={cn(
              "inline-flex h-6 items-center gap-1.5 rounded-md px-2 text-[11px] transition-colors",
              active ? "bg-ui-surface-raised text-ui-text-primary" : "text-ui-text-secondary",
              disabled ? "cursor-not-allowed opacity-40" : "hover:text-ui-text-primary",
            )}
          >
            {chip.label}
            {/* Ширина под две цифры: во время поиска счётчики меняются на каждый символ,
                и по месту цифры чипы разъезжались бы прямо под курсором. */}
            <span className="min-w-[1.25rem] text-[10px] text-ui-text-muted tabular-nums">{chip.count}</span>
          </button>
        )
      })}
    </span>
  )
}
